<template>
  <AsidePanel
    ref="asidePanel"
    :cancelText="cancelText"
    :components="components"
    :handleMountedCallback="handleMountedCallback"
    :handleSave="handleSave"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :navigateToTab="navigateToTab"
    :redirectTo="directoryTypeRoute(itemType)"
    :saveText="saveText"
    :showSaveButton="showSaveButton"
    :viewport="viewport"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="microsoft"
      />
    </template>
    <template v-slot:headerText>
      <EditableDirectoryName
        :itemType="itemType"
      />
    </template>
  </AsidePanel>
</template>

<script>
/**
 * @todo: Investigate creating a component for the GSuiteAside and
 * O365Aside - they share a lot of code
 */
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import Constants, {
  DirectoryActionConsts,
  DirectoryNameConsts,
  DirectoryTypeConsts,
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  StoreModelMap,
  UnsavedChangesModalConsts,
} from 'client/util/Constants';
import DirectoryDialogsHelper from 'client/directories/DirectoryDialogsHelper';
import EditableDirectoryName from 'client/directories/components/EditableDirectoryName.vue';
import ExportAttributeWarningModal from 'client/directories/components/ExportAttributeWarningModal';
import LocalStorageService from 'client/util/LocalStorageService';
import ModalEventBus from 'components/Modal/ModalEventBus';
import O365Details from 'client/directories/Office365/O365Details.vue';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemUserItemTable from 'client/systemusers/SystemUserItemTable/SystemUserItemTable.vue';
import UserGroupItemTable from 'client/components/ItemTable/UserGroupItemTable.vue';
import Util from 'client/util/Util';
import directoryRouteMixin from 'client/components/mixins/directoryRouteMixin';
import pluralize from 'pluralize';

const { create, update } = ItemActionConsts;
const {
  office365,
  systemUser,
  userGroup,
} = ItemTypeConsts;
const displayName = ItemTypeDisplayNames[office365];
const o365Model = StoreModelMap[office365];

export default {
  name: 'O365AsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
    EditableDirectoryName,
  },

  mixins: [
    AsidePanelPropsMixin,
    directoryRouteMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: office365,
    },
  },

  beforeRouteEnter(to, from, next) {
    const activated = LocalStorageService.getItem('cloudDirectoryActivated');
    const previousRoutes = from.matched.length;
    const reactivated = LocalStorageService.getItem('cloudDirectoryReactivated');
    const typeName = DirectoryNameConsts[DirectoryTypeConsts.office365];

    if (previousRoutes >= 0 && activated) {
      LocalStorageService.setItem('cloudDirectoryActivated', DirectoryActionConsts.activated);
    }

    if (previousRoutes === 0 && reactivated) {
      notify(
        Constants.success,
        `The ${typeName} Directory was successfully ${DirectoryActionConsts.reactivated}.`,
      );
    }

    next();
  },

  data() {
    return {
      cancelText: '',
      components: [{
        label: 'Details',
        vueComponent: O365Details,
        vueComponentProps: {
          handleOauthRedirect: this.handleOauthRedirect,
          itemId: this.itemId,
          viewport: this.viewport,
        },
      }],
      isInOauthRedirect: false,
      itemHasBeenSaved: false,
      navigateToTab: undefined,
      saveText: '',
      showSaveButton: false,
    };
  },

  computed: {
    ...mapGetters(o365Model, [
      'displayName',
      'getExportAttributesKbUrl',
      'hasItemChanged',
      'haveAssociationsChanged',
      'haveExportAttributesChanged',
      'isItemBeingCreated',
      'isItemBeingUpdated',
    ]),
  },

  mounted() {
    const activated = LocalStorageService.getItem('cloudDirectoryActivated');
    if (ModalEventBus.isModalOpen()) {
      ModalEventBus.close();
    }
    if (activated === DirectoryActionConsts.activated) {
      DirectoryDialogsHelper.showDirectoryImportUsersModal(
        DirectoryTypeConsts.office365,
        this.itemId,
      );
      LocalStorageService.removeItem('cloudDirectoryActivated');
    }
  },

  beforeDestroy() {
    this.handleBeforeRedirect();
  },

  methods: {
    ...mapActions(o365Model, [
      'bindAssociations',
      'saveExportAttributes',
      'saveItem',
    ]),

    handleBeforeRedirect() {
      LocalStorageService.removeItem('cloudDirectoryActivated');
      LocalStorageService.removeItem('cloudDirectoryReactivated');
    },

    handleMountedCallback() {
      if (this.isItemBeingCreated) {
        this.cancelText = 'close';
      } else if (this.isItemBeingUpdated) {
        const userGroupHeaderLabel = `Members of the following user groups are syncing to the ${displayName} Instance`;
        const userHeaderLabel = `The following users are syncing to the ${displayName} Instance`;

        this.cancelText = 'cancel';
        this.saveText = 'save';
        this.showSaveButton = true;

        this.components.push({
          itemType: userGroup,
          label: 'User Groups',
          headerLabels: {
            [create]: userGroupHeaderLabel,
            [update]: userGroupHeaderLabel,
            useDisplayName: false,
          },
          vueComponent: UserGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: `/api/v2/office365s/${this.itemId}/usergroups`,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.directoryAsidePanel,
          },
        }, {
          itemType: systemUser,
          label: 'Users',
          headerLabels: {
            [create]: userHeaderLabel,
            [update]: userHeaderLabel,
            useDisplayName: false,
          },
          vueComponent: SystemUserItemTable,
          vueComponentProps: {
            boundItemsUrl: `/api/v2/office365s/${this.itemId}/users`,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.directoryAsidePanel,
          },
        });
      }
    },

    handleOauthRedirect(oauthUrl) {
      const { bodyText, headerText } = UnsavedChangesModalConsts;

      const handleCancel = () => {
        ModalEventBus.close();
        this.isInOauthRedirect = false;
      };

      const componentProps = {
        bodyText,
        handleCancel: () => handleCancel(),
        handleContinue: () => Util.redirect(oauthUrl),
        headerText,
        showClose: true,
      };

      this.handleBeforeRedirect();

      if (this.hasModelChanged()) {
        // This flag prevents an additional chrome created modal message on redirect
        this.isInOauthRedirect = true;
        ModalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
          preventCloseOnOverlayClick: true,
        });
      } else {
        try {
          Util.redirect(oauthUrl);
        } catch {
          this.isInOauthRedirect = false;
        }
      }
    },

    async handleSave() {
      const saveData = () => {
        this.itemHasBeenSaved = true;
        const parentType = office365;

        return this.saveItem()
          .then(() => this.saveExportAttributes())
          .then(() => this.bindAssociations({ parentType, targetType: userGroup }))
          .then(() => this.bindAssociations({ parentType, targetType: systemUser }))
          .then(() => {
            const canContinue = false;
            this.navigateToUserGroupsTab();
            return new SaveReturnValue({ canContinue });
          })
          .catch((err) => {
            throw err instanceof Error ? err : Error('Unknown error');
          });
      };

      // Don't save if the directory name is invalid.
      if (this.v2Errors.any()) {
        throw new Error(this.v2Errors.items[0].msg);
      }

      if (this.haveExportAttributesChanged) {
        const canContinue = await this.showWarningModal();
        if (canContinue === false) {
          this.navigateToUserGroupsTab();
          return new SaveReturnValue({ canContinue });
        }
      }

      return saveData();
    },

    hasModelChanged() {
      if (this.isInOauthRedirect) {
        return false;
      }

      if (this.itemHasBeenSaved) {
        return false;
      }

      return this.haveExportAttributesChanged
        || this.haveAssociationsChanged([userGroup, systemUser])
        || this.hasItemChanged;
    },

    navigateToUserGroupsTab() {
      const userGroupsTabLabel = pluralize(ItemTypeDisplayNames[ItemTypeConsts.userGroup]);
      this.navigateToTab = { tabLabel: userGroupsTabLabel };
    },

    showWarningModal() {
      return new Promise((resolve) => {
        ModalEventBus.open({
          component: ExportAttributeWarningModal,
          componentProps: {
            directoryName: this.displayName,
            exportAttributesKbUrl: this.getExportAttributesKbUrl,
            handleCancel: () => resolve(false),
            handleContinue: () => resolve(true),
          },
        });
      });
    },
  },
};
</script>

<style module>
.instructions {
  margin-top: 64px;
}
</style>
